import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import BoxSection from './BoxSection'
import { useCurrentWorkspaceDetailQuery, useCurrentWorkspacePaymentMethod } from '../../../hooks/queries'
import { customDayjs } from '../../../utils/date'
import PriceFormat from '../../Text/PriceFormater'
import AivatarButton from '../../buttons/AivatarButton'
import useChangePaymentMethod from '../../../hooks/useChangePaymentMethod'
import { paymentsQueries } from '../../../services/payments'

/** 갱신일까지 이 일수 이내면 상시 노출한다 (BE 고지 단계 30,7 의 가장 큰 단계) */
const NOTICE_WINDOW_DAYS = 30

type Props = {
  className?: string
  onClickUnsubscribe: () => void
}

/**
 * 갱신 사전 고지 (기획 전달본 S5).
 *
 * 연간은 금액이 크고 1년에 한 번만 청구되므로 고지 없는 자동 갱신이 분쟁의 직접 대상이다.
 * 메일이 도달하지 않는 경우가 있어 화면 표기가 예비선이다.
 */
export default function RenewalNoticeSection({ className, onClickUnsubscribe }: Props) {
  const { data: workspace } = useCurrentWorkspaceDetailQuery()
  const { data: paymentMethod } = useCurrentWorkspacePaymentMethod()
  const { t } = useTranslation('subscription')
  const { changePaymentMethod } = useChangePaymentMethod()

  const { data: notices } = useQuery({
    ...paymentsQueries.renewalNotices(Number(workspace?.id)),
    enabled: !!workspace?.id,
    retry: false,
  })

  const subscription = workspace?.subscription
  const next = workspace?.nextSubscription

  // 해지 예약·수기 결제·결제수단 없음은 자동 갱신 대상이 아니다.
  const autoRenews =
    subscription?.status === 'ACTIVE' &&
    next?.status !== 'PAUSE' &&
    !subscription?.isManualPayment &&
    !!paymentMethod

  const renewAt = subscription?.endAt ? customDayjs(subscription.endAt).add(1, 'day').startOf('day') : null
  const daysLeft = renewAt ? renewAt.diff(customDayjs().startOf('day'), 'day') : null

  if (!autoRenews || daysLeft == null || daysLeft < 0 || daysLeft > NOTICE_WINDOW_DAYS) return null

  // 예약된 변경이 있으면 갱신 시점에 적용되는 것은 그쪽이다. "현재 플랜"이라고 부르지 않는다.
  const renewingPlan = next?.status === 'CHANGED' ? next : subscription
  const latestNotice = notices?.[0]

  return (
    <BoxSection className={className}>
      <span className="bg-main-4 text-main-1 inline-block rounded-[500px] px-[12px] py-[3px] text-[12px] font-[700]">
        {t('갱신 예정')}
      </span>

      {/* 고지는 구간 방식이라 "30일 단계"가 실제로는 21일 전에 나갈 수 있다.
          절대 날짜를 기본으로 하고 상대 일수는 실제 남은 일수를 쓴다 (BE T-D5 값 계약) */}
      <p className="text-gray-2 mt-[12px] break-keep text-[14px] font-[700]">
        {t('{{date}}에 {{plan_name}}으로 자동 갱신됩니다. {{days}}일 남았습니다.', {
          date: renewAt?.format('YYYY-MM-DD'),
          plan_name: renewingPlan?.plan?.displayName,
          days: daysLeft,
        })}
      </p>
      <p className="text-gray-21 mt-[6px] break-keep text-[12px]">
        {t('결제 예정 금액')} <PriceFormat amount={renewingPlan?.price ?? 0} /> {t('(부가세 포함)')} ·{' '}
        {t('등록된 결제 수단으로 자동 결제됩니다.')}
      </p>
      {latestNotice?.notifiedAt ? (
        <p className="text-gray-3 mt-[4px] text-[12px]">
          {t('최근 고지 발송')}: {customDayjs(latestNotice.notifiedAt).format('YYYY-MM-DD')}
        </p>
      ) : null}

      <div className="mt-[16px] flex items-center gap-[10px]">
        <AivatarButton type="button" variant="m4" onClick={() => changePaymentMethod()}>
          {t('결제 수단 변경')}
        </AivatarButton>
        <button type="button" className="text-hyperlink text-[13px]" onClick={onClickUnsubscribe}>
          {t('구독 해지')}
        </button>
      </div>
    </BoxSection>
  )
}
